// src/admin/orgProvisioning.ts
/**
 * Pure decisions behind the admin-created-organizations screens (spec §7): form validation,
 * error-to-message mapping, idempotency-key retention, the feature probe, the staff picker filter,
 * invitation row state, the org detail page's ordered invitations reads and the two header
 * badges. Kept free of React so the whole surface is testable under the `node` vitest
 * environment; the components only render what these return.
 */
import type {
  AdminInvitation,
  AdminOrganization,
  AdminUser,
  CreateOrganizationRequest,
  InvitationRole,
  OrganizationIdentityProviderKind,
  StaffMemberRole,
} from '../services/adminApi';
import { formatDate } from './formatDate';

/** The ButtonUp organization whose Owners and Admins are platform staff (ParsleeTenantGuard). */
export const STAFF_ORGANIZATION_ID = 'org_button_up';

export const IDENTITY_PROVIDER_OPTIONS: { value: OrganizationIdentityProviderKind; label: string }[] = [
  { value: 'Microsoft365', label: 'Microsoft 365' },
  { value: 'GoogleWorkspace', label: 'Google Workspace' },
  { value: 'None', label: 'Other' },
];

export const INVITATION_ROLES: InvitationRole[] = ['Owner', 'Admin', 'Member'];
export const STAFF_MEMBER_ROLES: StaffMemberRole[] = ['Admin', 'Member'];

export interface CreateOrganizationFormValues {
  name: string;
  displayName: string;
  identityProvider: OrganizationIdentityProviderKind;
  workEmailDomain: string;
  billingEmail: string;
}

export const EMPTY_CREATE_ORGANIZATION_FORM: CreateOrganizationFormValues = {
  name: '',
  displayName: '',
  identityProvider: 'Microsoft365',
  workEmailDomain: '',
  billingEmail: '',
};

export type CreateOrganizationFieldErrors = Partial<Record<keyof CreateOrganizationFormValues, string>>;

export type CreateOrganizationValidation =
  | { ok: true; request: Omit<CreateOrganizationRequest, 'requestId'> }
  | { ok: false; errors: CreateOrganizationFieldErrors };

// Strict hostname (spec §6.1): labels of letters, digits and hyphens, at least one dot, no @,
// scheme or path. Length is checked separately against the 253-character limit.
const HOSTNAME_PATTERN = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;
const NUMERIC_LABEL_PATTERN = /^[0-9]+$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function normalizeWorkEmailDomain(raw: string): string {
  return raw.trim().toLowerCase();
}

export function validateCreateOrganizationForm(values: CreateOrganizationFormValues): CreateOrganizationValidation {
  const errors: CreateOrganizationFieldErrors = {};
  const name = values.name.trim();
  if (!name) errors.name = 'Enter the company name.';

  const domain = normalizeWorkEmailDomain(values.workEmailDomain);
  if (!domain || domain.length > 253 || !HOSTNAME_PATTERN.test(domain)) {
    errors.workEmailDomain = 'Enter a bare domain like company.example (no @, no https://, no path).';
  } else if (NUMERIC_LABEL_PATTERN.test(domain.slice(domain.lastIndexOf('.') + 1))) {
    // The server's WorkEmailDomain.TryNormalize refuses an all-digit last label: an IP literal
    // (10.0.0.1) or no real top-level domain (company.123).
    errors.workEmailDomain = 'Enter a domain that ends in a name like .com, not a number. IP addresses are not accepted.';
  }

  const billingEmail = values.billingEmail.trim();
  if (billingEmail && !EMAIL_PATTERN.test(billingEmail)) {
    errors.billingEmail = 'Enter a valid email address.';
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return {
    ok: true,
    request: {
      name,
      displayName: values.displayName.trim() || name,
      identityProvider: values.identityProvider,
      workEmailDomain: domain,
      billingEmail,
    },
  };
}

/** The display name mirrors the company name until the staff member edits it by hand. */
export function nextDisplayName(name: string, displayName: string, displayNameTouched: boolean): string {
  return displayNameTouched ? displayName : name;
}

/** Form order, used to focus the first invalid field after a failed submit. */
export const CREATE_ORGANIZATION_FIELD_ORDER: (keyof CreateOrganizationFormValues)[] = [
  'name',
  'displayName',
  'identityProvider',
  'workEmailDomain',
  'billingEmail',
];

export function firstInvalidField(errors: CreateOrganizationFieldErrors): keyof CreateOrganizationFormValues | null {
  return CREATE_ORGANIZATION_FIELD_ORDER.find((field) => Boolean(errors[field])) ?? null;
}

/** Editing a field clears its own error; the others stay until the next submit. */
export function clearFieldError(
  errors: CreateOrganizationFieldErrors,
  field: keyof CreateOrganizationFormValues,
): CreateOrganizationFieldErrors {
  if (!errors[field]) return errors;
  const next = { ...errors };
  delete next[field];
  return next;
}

export interface ApiFailure {
  status?: number;
  code?: string;
  detail?: string;
  data: Record<string, unknown>;
}

// The axios response interceptor does not camelCase error bodies and ProblemDetails extension keys
// are written verbatim, so probe both casings (same reasoning as readApiError in OrgBillingTab).
// Some m365 answers put prose where a code goes (404 `{ error: "Organization not found" }`), and
// some bodies are a bare string or empty, so only a snake_case token counts as a code; prose is
// offered as the detail instead, and every 404 is mapped on the status, never on the body.
const ERROR_CODE_PATTERN = /^[a-z][a-z0-9_]*$/;

function readProse(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const text = value.trim();
  return text && !text.startsWith('<') ? text : undefined;
}

export function readApiFailure(err: unknown): ApiFailure {
  const e = err as { response?: { status?: number; data?: unknown } };
  const raw = e?.response?.data;
  const data = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const rawCode = data['error'] ?? data['Error'];
  const code = typeof rawCode === 'string' && ERROR_CODE_PATTERN.test(rawCode) ? rawCode : undefined;
  const detail =
    readProse(data['detail'] ?? data['Detail'] ?? data['message'] ?? data['Message']) ??
    (code === undefined ? readProse(rawCode) : undefined) ??
    readProse(raw);
  return { status: e?.response?.status, code, detail, data };
}

function fallbackMessage(err: unknown, message: string): string {
  return err instanceof Error && err.message ? err.message : message;
}

export const REQUEST_BODY_REQUIRED_MESSAGE = 'The request reached the server empty. Reload the page and try again.';
export const ACCOUNT_CONTEXT_MISSING_MESSAGE =
  'Your sign-in no longer identifies a ButtonUp account. Sign in again, then retry.';

/** Answers every endpoint shares: 400 request_body_required and 401 (account_context_missing or prose). */
export function genericFailureMessage(status: number | undefined, code: string | undefined): string | null {
  if (status === 401 || code === 'account_context_missing') return ACCOUNT_CONTEXT_MISSING_MESSAGE;
  if (code === 'request_body_required') return REQUEST_BODY_REQUIRED_MESSAGE;
  return null;
}

export interface OrganizationMatch {
  id: string;
  displayName: string;
}

/** The organizations a 409 organization_exists names (spec §6.1 "listing the matches' ids and display names"). */
export function readOrganizationMatches(data: Record<string, unknown>): OrganizationMatch[] {
  const raw = data['matches'] ?? data['Matches'] ?? data['organizations'] ?? data['Organizations'];
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const row = item as Record<string, unknown>;
    const id = (row['id'] ?? row['Id']) as string | undefined;
    if (!id) return [];
    const displayName = (row['displayName'] ?? row['DisplayName'] ?? row['name'] ?? row['Name'] ?? id) as string;
    return [{ id, displayName }];
  });
}

export type CreateOrganizationFailure =
  | { kind: 'conflict'; message: string; matches: OrganizationMatch[] }
  | { kind: 'in_progress'; message: string }
  | { kind: 'not_microsoft_tenant'; message: string; focusField: 'identityProvider' }
  | { kind: 'lookup_failed'; message: string }
  | { kind: 'field'; field: 'workEmailDomain'; message: string }
  | { kind: 'ambiguous'; message: string; organizationId?: string }
  | { kind: 'other'; message: string };

// Reached only once createWithInProgressRetry has spent its attempts. A requestId claim stranded
// by a server crash answers request_in_progress forever, so the next submit mints a new key
// (keepRequestIdAfterFailure); the server's domain claim and duplicate guard still stop a second
// organization for the same domain.
export const CREATE_IN_PROGRESS_MESSAGE =
  'Another create for this domain is still running, or an earlier attempt stopped part-way. Retry starts a new request; if the organization was created meanwhile, it is shown as a match instead of being created twice.';

// A 5xx, a timeout or a network failure may have reached the server. The key is kept
// (keepRequestIdAfterFailure), so an unchanged resubmit replays it and finishes provisioning.
export const CREATE_AMBIGUOUS_MESSAGE =
  'The create may have reached the server. Press Create organization again without changing anything to finish it.';

export function mapCreateOrganizationFailure(err: unknown, domain: string): CreateOrganizationFailure {
  const { status, code, detail, data } = readApiFailure(err);
  if (isCreateInProgress(err)) {
    return { kind: 'in_progress', message: CREATE_IN_PROGRESS_MESSAGE };
  }
  if (status === 409) {
    const matches = readOrganizationMatches(data);
    const owner = matches.length > 0 ? matches.map((m) => m.displayName).join(', ') : 'another organization';
    return { kind: 'conflict', message: `${domain} already belongs to ${owner}`, matches };
  }
  if (status === 422 || code === 'domain_not_microsoft_tenant') {
    return {
      kind: 'not_microsoft_tenant',
      message: `${domain} is not a Microsoft 365 tenant. Choose a different identity provider or correct the domain.`,
      focusField: 'identityProvider',
    };
  }
  if (status === 502 || code === 'tenant_lookup_failed') {
    return { kind: 'lookup_failed', message: 'Could not verify the Microsoft tenant. Try again.' };
  }
  if (status === undefined || status >= 500) {
    const organizationId = data['organizationId'] ?? data['OrganizationId'];
    return typeof organizationId === 'string' && organizationId
      ? { kind: 'ambiguous', message: CREATE_AMBIGUOUS_MESSAGE, organizationId }
      : { kind: 'ambiguous', message: CREATE_AMBIGUOUS_MESSAGE };
  }
  if (status === 404) {
    return { kind: 'other', message: 'This API does not support creating organizations from the dashboard yet.' };
  }
  if (code === 'work_email_domain_invalid') {
    return {
      kind: 'field',
      field: 'workEmailDomain',
      message: 'The server did not accept this domain. Enter a bare domain like company.example (no @, no https://, no path).',
    };
  }
  const generic = genericFailureMessage(status, code);
  if (generic) return { kind: 'other', message: generic };
  return { kind: 'other', message: detail || fallbackMessage(err, 'Failed to create the organization.') };
}

/**
 * Idempotency keys (spec §6.1, §6.3, §11): a 5xx or a network failure may have reached the server,
 * so the retry replays the same key; after a 4xx the input is about to change, so the key rotates.
 * 409 request_in_progress rotates too: createWithInProgressRetry already presented the same key
 * through its attempts, and a failure that reaches the caller means they are spent; a requestId
 * claim stranded by a server crash would answer request_in_progress to that key forever.
 */
export function keepRequestIdAfterFailure(status: number | undefined, _code?: string): boolean {
  return status === undefined || status >= 500;
}

/**
 * Send keys additionally survive the rate-limit answers: the server checks the cooldown before
 * the idempotency claim, so a replay after a lost response must still present the original key
 * (it then answers send_already_processed, a candidate the reconcile confirms). They also
 * survive 409 send_in_progress: the server released this key's claim, so the retry after the
 * other staff member's send finishes presents the same key.
 */
export function keepSendRequestIdAfterFailure(status: number | undefined, code?: string): boolean {
  return (
    keepRequestIdAfterFailure(status, code) ||
    status === 429 ||
    code === 'send_too_soon' ||
    code === 'send_limit_reached' ||
    code === 'send_in_progress'
  );
}

/** 409 request_in_progress: another staff create for the same requestId or domain is still running. */
export function isCreateInProgress(err: unknown): boolean {
  const { status, code } = readApiFailure(err);
  return status === 409 && code === 'request_in_progress';
}

export const CREATE_IN_PROGRESS_RETRY_ATTEMPTS = 5;
export const CREATE_IN_PROGRESS_RETRY_DELAY_MS = 2000;

/**
 * Runs `attempt` with one requestId, waiting `delayMs` and trying again only on 409
 * request_in_progress, up to `attempts` tries in all. Every other outcome, success or failure, is
 * returned or thrown unchanged, so a 409 organization_exists after the winner finishes reaches the
 * caller's normal mapping. `onWaiting(n)` fires before each wait with the attempt that just failed.
 */
export async function createWithInProgressRetry<T>(
  attempt: (requestId: string) => Promise<T>,
  requestId: string,
  onWaiting: (attemptNumber: number) => void,
  options: { attempts?: number; delayMs?: number } = {},
): Promise<T> {
  const attempts = options.attempts ?? CREATE_IN_PROGRESS_RETRY_ATTEMPTS;
  const delayMs = options.delayMs ?? CREATE_IN_PROGRESS_RETRY_DELAY_MS;
  for (let n = 1; ; n += 1) {
    try {
      return await attempt(requestId);
    } catch (err: unknown) {
      if (!isCreateInProgress(err) || n >= attempts) throw err;
      onWaiting(n);
      await new Promise<void>((resolve) => { setTimeout(resolve, delayMs); });
    }
  }
}

/** A create key and the normalized payload it was first sent with. */
export interface CreateSubmission {
  requestId: string;
  payloadKey: string;
}

/**
 * The key for the next create. The backend replays by requestId alone and returns the original
 * organization without comparing payloads, so a key may only be reused while the normalized
 * payload is unchanged; any edit mints a new one.
 */
export function submissionFor(
  previous: CreateSubmission | null,
  request: Omit<CreateOrganizationRequest, 'requestId'>,
  mint: () => string,
): CreateSubmission {
  const payloadKey = JSON.stringify(request);
  return previous !== null && previous.payloadKey === payloadKey ? previous : { requestId: mint(), payloadKey };
}

export type InvitationsAvailability = 'unknown' | 'supported' | 'unsupported' | 'error';

/** The invitations list doubles as the feature probe: a 404 means the API predates the feature. */
export function resolveInvitationsAvailability(err: unknown): 'unsupported' | 'error' {
  return readApiFailure(err).status === 404 ? 'unsupported' : 'error';
}

/**
 * Staff are Active Owners and Admins of the ButtonUp org (the users DTO lower-cases roles), minus
 * accounts that already hold a membership in the target org, whatever its status: the add route
 * answers 409 when one exists.
 */
export function selectableStaff(users: AdminUser[], excludeAccountIds: Iterable<string> = []): AdminUser[] {
  const excluded = new Set(excludeAccountIds);
  return users
    .filter((u) => !excluded.has(u.id) && u.status === 'Active' && ['owner', 'admin'].includes(u.role.toLowerCase()))
    .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
}

export function normalizeInvitationRole(role: string | null | undefined): InvitationRole {
  const wanted = (role ?? '').toLowerCase();
  return INVITATION_ROLES.find((r) => r.toLowerCase() === wanted) ?? 'Owner';
}

export type InvitationRowAction = 'send' | 'resend' | 'copy' | 'revoke' | 'new';

export function isInvitationExpired(inv: Pick<AdminInvitation, 'status' | 'expiresAt'>, now: Date): boolean {
  const status = inv.status.toLowerCase();
  if (status === 'expired') return true;
  if (status !== 'pending') return false;
  const expires = new Date(inv.expiresAt).getTime();
  return Number.isFinite(expires) && expires <= now.getTime();
}

function statusLabel(status: string): string {
  switch (status.toLowerCase()) {
    case 'pending': return 'Pending';
    case 'accepted': return 'Accepted';
    case 'revoked': return 'Revoked';
    case 'autoresolved': return 'Joined by domain';
    case 'expired': return 'Expired';
    default: return status;
  }
}

export function invitationStatusLabel(inv: Pick<AdminInvitation, 'status' | 'expiresAt'>, now: Date): string {
  if (isInvitationExpired(inv, now)) return 'Expired';
  return statusLabel(inv.status);
}

export function invitationRowActions(
  inv: Pick<AdminInvitation, 'status' | 'expiresAt' | 'acceptUrl' | 'sendCount'>,
  now: Date,
): InvitationRowAction[] {
  if (isInvitationExpired(inv, now)) return ['new'];
  if (inv.status.toLowerCase() !== 'pending') return [];
  const actions: InvitationRowAction[] = [inv.sendCount > 0 ? 'resend' : 'send'];
  if (inv.acceptUrl) actions.push('copy');
  actions.push('revoke');
  return actions;
}

/**
 * The invitation's accept link while it can still be used (pending, unexpired, with a link), else
 * null. Both views and the page's `currentAcceptUrl` callback read the link through this, so a row or a
 * send DTO that turned Accepted, AutoResolved, Revoked or Expired never offers or shows a dead link.
 */
export function usableAcceptUrl(
  inv: Pick<AdminInvitation, 'status' | 'expiresAt' | 'acceptUrl' | 'sendCount'>,
  now: Date,
): string | null {
  return invitationRowActions(inv, now).includes('copy') ? inv.acceptUrl : null;
}

/** The page's invitations list, tagged with the org it was read or reset for. */
export interface OrgInvitations {
  orgId: string | undefined;
  list: AdminInvitation[];
}

/**
 * The list for `orgId`, or an empty one when the list belongs to another org: after a back/forward
 * org switch the route changes before the org-change reset lands, and in between the previous org's
 * rows must neither paint nor be served to the new org's controller.
 */
export function invitationsForOrg(invitations: OrgInvitations, orgId: string | undefined): AdminInvitation[] {
  return invitations.orgId === orgId ? invitations.list : [];
}

/**
 * The page's row for `invitationId`, read for the controller of `forOrgId`. Undefined unless
 * `forOrgId` is the org the page shows and the list was read for it, so a controller that settles
 * after the route moved on never sees another org's row. Backs the page's `currentInvitation` and
 * `currentAcceptUrl`.
 */
export function pageInvitation(
  page: { currentOrgId: string | undefined; invitations: OrgInvitations },
  forOrgId: string,
  invitationId: string,
): AdminInvitation | undefined {
  if (forOrgId !== page.currentOrgId) return undefined;
  return invitationsForOrg(page.invitations, forOrgId).find((inv) => inv.id === invitationId);
}

// Token-only badge classes, keyed by the label invitationStatusLabel returns; shared by the
// panel's rows and the invite dialog's created state.
const INVITATION_STATUS_BADGE_CLASS: Record<string, string> = {
  Pending: 'border-transparent bg-warning/10 text-warning',
  Accepted: 'border-transparent bg-success/10 text-success',
  'Joined by domain': 'border-transparent bg-success/10 text-success',
  Expired: 'border-transparent bg-muted text-text-secondary',
  Revoked: 'border-transparent bg-muted text-text-secondary',
};

export function invitationStatusBadgeClass(label: string): string {
  return INVITATION_STATUS_BADGE_CLASS[label] || INVITATION_STATUS_BADGE_CLASS.Expired;
}

export function formatLastSent(inv: Pick<AdminInvitation, 'lastSentAt' | 'sendCount'>): string {
  if (!inv.lastSentAt || inv.sendCount <= 0) return 'Never';
  return `${formatDate(inv.lastSentAt)} (${inv.sendCount} sent)`;
}

export interface ProvisioningBadge {
  key: 'terms' | 'owner';
  label: string;
  /** Tooltip: what clears the badge. */
  title: string;
  className: string;
}

// Both are "waiting on the customer" states, not faults, so both use the warning tint (the same
// token-only badge idiom as the role/status badges in OrgDetailPage); red is reserved for faults.
export const PROVISIONING_BADGE_CLASS = 'border-transparent bg-warning/10 text-warning';

/** Header badges (spec §7). Both key on explicit values so an older API never shows them. */
export function orgProvisioningBadges(
  org: Pick<AdminOrganization, 'customerTermsPending' | 'ownershipEstablished'>,
): ProvisioningBadge[] {
  const badges: ProvisioningBadge[] = [];
  if (org.customerTermsPending === true) {
    badges.push({
      key: 'terms',
      label: 'Terms: pending customer acceptance',
      title: 'Clears when the first non-staff member accepts the terms, on the invitation page or at first sign-in. Staff never count.',
      className: PROVISIONING_BADGE_CLASS,
    });
  }
  if (org.ownershipEstablished === false) {
    badges.push({
      key: 'owner',
      label: 'Owner: none yet',
      title: 'Clears when a non-staff Owner exists: an invited Owner accepts or joins by domain, or staff promote a member from the Users tab.',
      className: PROVISIONING_BADGE_CLASS,
    });
  }
  return badges;
}

export function mapAddStaffFailure(err: unknown): string {
  const { status, code, detail } = readApiFailure(err);
  const generic = genericFailureMessage(status, code);
  if (generic) return generic;
  if (status === 403 || code === 'staff_only') {
    return 'That account is not ButtonUp staff: it needs an Owner or Admin membership in the ButtonUp organization.';
  }
  if (status === 409) return 'That person is already a member of this organization.';
  if (code === 'account_not_found') return 'That staff account no longer exists. Reload the list and try again.';
  if (status === 404) {
    // The button shows only once the invitations probe answered, so a 404 with a body is the
    // route's own answer ("Organization not found"); only an empty one is an older API.
    return detail || 'This API does not support adding staff from the dashboard yet.';
  }
  return detail || fallbackMessage(err, 'Failed to add the staff member.');
}

export function mapCreateInvitationFailure(err: unknown, email: string): string {
  const { status, code, detail } = readApiFailure(err);
  const generic = genericFailureMessage(status, code);
  if (generic) return generic;
  if (status === 409) return `A pending invitation for ${email} already exists. Resend it from the list, or revoke it first.`;
  if (status === 400) return detail || 'Enter a valid email address.';
  if (status === 404) {
    // The invite button shows only once the invitations probe answered, so a 404 with a body is
    // the route's own answer ("Organization not found"); only an empty one is an older API.
    return detail || 'This API does not support inviting customers from the dashboard yet.';
  }
  return detail || fallbackMessage(err, 'Failed to create the invitation.');
}

export const INVITATION_CHANGED_MESSAGE = 'This invitation changed while you were working on it. Refresh the list.';

/**
 * The label for the `status` a 409 invitation_not_pending / invitation_not_sendable carries (the
 * enum name), or null without one. A Pending refusal with the body's `expiresAt` is the expiry
 * check (`RefuseUnsendableInvitation`); one without it lost a race with a revoke or an accept
 * (the ETag-conditioned touch), so the row has changed and only a refresh can say how.
 */
function refusedInvitationStatus(data: Record<string, unknown>): string | 'changed' | null {
  const status = data['status'] ?? data['Status'];
  if (typeof status !== 'string' || !status) return null;
  if (status.toLowerCase() === 'pending') {
    const expiresAt = data['expiresAt'] ?? data['ExpiresAt'];
    return typeof expiresAt === 'string' && expiresAt ? 'Expired' : 'changed';
  }
  return statusLabel(status);
}

export function mapInvitationActionFailure(err: unknown, action: 'send' | 'revoke'): string {
  const { status, code, detail, data } = readApiFailure(err);
  if (status === 409) {
    const refused = refusedInvitationStatus(data);
    if (refused === 'changed') return INVITATION_CHANGED_MESSAGE;
    if (action === 'send') {
      return refused
        ? `This invitation can no longer be sent (status: ${refused}). Create a new invitation.`
        : 'This invitation can no longer be sent: it is not pending or it has expired. Create a new invitation.';
    }
    return refused ? `This invitation is no longer pending (status: ${refused}).` : 'This invitation is no longer pending.';
  }
  if (status === 404) return 'This invitation no longer exists. Refresh the list.';
  const generic = genericFailureMessage(status, code);
  if (generic) return generic;
  return detail || fallbackMessage(err, action === 'send' ? 'Failed to send the invitation.' : 'Failed to revoke the invitation.');
}

// ---- Send rate limits (m365 send answers: 429 send_too_soon, 409 send_limit_reached, 409 send_in_progress) ----

/**
 * 'rate_limited': 429 invitation_send_rate_limited, the email service refused the message, so
 * nothing was sent. 'unspecified': a 429 with no code we know (a gateway, say), worded neutrally.
 * Absent: 429 send_too_soon, the server's cooldown after a send, the only one worded "Sent".
 */
export type SendCooldownCause = 'rate_limited' | 'unspecified';

/**
 * Why the server may still hold this invitation's send lock (up to 1 h) after a send whose
 * answer the dashboard saw: 'unknown' after invitation_send_outcome_unknown (or an unconfirmed
 * send_already_processed), 'unrecorded' after invitation_stamp_failed (sent, not stamped).
 * Tracked per invitation by the controller with nextSendLockCause.
 */
export type SendLockCause = 'unknown' | 'unrecorded' | null;

export type SendBlock =
  | { kind: 'cooldown'; secondsLeft: number; cause?: SendCooldownCause }
  | { kind: 'limit'; sendCount: number; limit: number }
  | { kind: 'in_progress' };

export type SendFailure =
  | { kind: 'too_soon'; retryAfterSeconds: number; cause?: SendCooldownCause }
  | { kind: 'limit_reached'; sendCount: number; limit: number }
  | { kind: 'in_progress' }
  | { kind: 'already_sent' }
  // 502 invitation_send_outcome_unknown, and send_in_progress while that lock is held.
  | { kind: 'outcome_unknown'; message: string }
  // 500 invitation_stamp_failed, and send_in_progress while that lock is held.
  | { kind: 'sent_unrecorded'; message: string }
  | { kind: 'other'; message: string };

const DEFAULT_SEND_COOLDOWN_SECONDS = 60;
const DEFAULT_SEND_LIMIT = 10;
// 500 invitation_stamp_failed: the server keeps the requestId claim and the send lock (up to 1 h).
export const SEND_NOT_RECORDED_MESSAGE =
  'The email was sent but the record could not be updated. Refresh the list before sending again; another send is blocked for up to an hour.';
export const SEND_IN_PROGRESS_MESSAGE = 'Another send for this invitation is in progress. Try again in a moment.';
export const SEND_OUTCOME_UNKNOWN_MESSAGE =
  'The invitation may have been sent. Check with the customer before sending again; another send is blocked for up to an hour.';
export const SEND_FAILED_MESSAGE = 'The email could not be sent, and nothing went out. Try again.';
export const SEND_LOCKED_AFTER_SENT_MESSAGE = 'The invitation was sent; another send is blocked for up to an hour.';
export const SEND_AMBIGUOUS_MESSAGE =
  'Could not confirm whether the invitation was sent. Refresh the list before sending again; a retry reuses the same request, so it cannot send a second copy.';

function readNumber(data: Record<string, unknown>, camel: string, pascal: string): number | null {
  const value = Number(data[camel] ?? data[pascal]);
  return Number.isFinite(value) ? value : null;
}

/**
 * The body field is the contract; the Retry-After header is not read.
 *
 * `lockCause`: this invitation's SendLockCause. While the server keeps the lock after an unknown
 * outcome or a stamp failure, a new key answers send_in_progress for up to 1 h, so that is worded
 * from the cause, never as a brief wait. send_already_processed is only a CANDIDATE success
 * whatever the cause: the throttles run before the claim, and a stamped first send makes a replay
 * within 60 s answer send_too_soon, so a replay that reaches the claim usually means the first
 * answer was never seen (outcome_unknown, stamp_failed, or still in flight). The controller
 * confirms it with sendCountIncreased after the awaited reconcile and words it with
 * alreadySentMessage. Nothing is retried automatically.
 */
export function mapSendFailure(err: unknown, options: { lockCause?: SendLockCause } = {}): SendFailure {
  const { status, code, data } = readApiFailure(err);
  if (code === 'invitation_send_outcome_unknown' || (code === 'send_in_progress' && options.lockCause === 'unknown')) {
    return { kind: 'outcome_unknown', message: SEND_OUTCOME_UNKNOWN_MESSAGE };
  }
  if (code === 'send_in_progress' && options.lockCause === 'unrecorded') {
    return { kind: 'sent_unrecorded', message: SEND_LOCKED_AFTER_SENT_MESSAGE };
  }
  if (status === 429 || code === 'send_too_soon' || code === 'invitation_send_rate_limited') {
    const retryAfterSeconds = readNumber(data, 'retryAfterSeconds', 'RetryAfterSeconds');
    const seconds =
      retryAfterSeconds !== null && retryAfterSeconds > 0 ? Math.ceil(retryAfterSeconds) : DEFAULT_SEND_COOLDOWN_SECONDS;
    if (code === 'send_too_soon') return { kind: 'too_soon', retryAfterSeconds: seconds };
    return {
      kind: 'too_soon',
      retryAfterSeconds: seconds,
      cause: code === 'invitation_send_rate_limited' ? 'rate_limited' : 'unspecified',
    };
  }
  if (status === 409 && code === 'send_limit_reached') {
    const limit = readNumber(data, 'limit', 'Limit') ?? DEFAULT_SEND_LIMIT;
    return { kind: 'limit_reached', sendCount: readNumber(data, 'sendCount', 'SendCount') ?? limit, limit };
  }
  if (code === 'send_in_progress') {
    // Another staff member's send for this invitation is in flight; the server released this
    // key's claim, so the same key is retried once that send finishes (nothing is resent here).
    return { kind: 'in_progress' };
  }
  if (code === 'send_already_processed') {
    // A candidate success: confirmed only by the reconcile (see above).
    return { kind: 'already_sent' };
  }
  if (code === 'invitation_stamp_failed' || data['emailSent'] === true || data['EmailSent'] === true) {
    // The email went out but LastSentAt / SendCount did not update; the claim and lock are kept
    // server-side, so a retry with the same key answers send_already_processed, never a 2nd email.
    return { kind: 'sent_unrecorded', message: SEND_NOT_RECORDED_MESSAGE };
  }
  if (code === 'invitation_send_failed') {
    // A definite non-send: the server released the claim, so the kept key retries safely.
    return { kind: 'other', message: SEND_FAILED_MESSAGE };
  }
  if (status === undefined || (status >= 500 && code === undefined)) {
    // No answer (a network error or the 30 s client timeout), an HTML 502 or a bare ingress 500:
    // the request may or may not have reached the send. The key is kept, so a retry replays it
    // and the server's claim stops a second email.
    return { kind: 'other', message: SEND_AMBIGUOUS_MESSAGE };
  }
  return { kind: 'other', message: mapInvitationActionFailure(err, 'send') };
}

/**
 * The invitation's SendLockCause after a send answer (`failure` null: the send returned its DTO).
 * outcome_unknown sets 'unknown'; sent_unrecorded sets 'unrecorded'; a DTO clears it.
 * already_sent clears it only when `confirmed` (sendCountIncreased after the awaited reconcile);
 * unconfirmed, it keeps 'unrecorded' (that send did go out) and otherwise sets 'unknown'.
 * Everything else proves nothing either way and keeps the previous cause.
 */
export function nextSendLockCause(
  previous: SendLockCause,
  failure: SendFailure | null,
  options: { confirmed?: boolean } = {},
): SendLockCause {
  if (failure === null) return null;
  if (failure.kind === 'already_sent') {
    if (options.confirmed) return null;
    return previous === 'unrecorded' ? 'unrecorded' : 'unknown';
  }
  if (failure.kind === 'outcome_unknown') return 'unknown';
  if (failure.kind === 'sent_unrecorded') return 'unrecorded';
  return previous;
}

/**
 * The action error for an already_sent candidate once the reconcile has settled, or null when it
 * is confirmed and the controller reports Sent. `previous` is the cause before this answer.
 */
export function alreadySentMessage(previous: SendLockCause, confirmed: boolean): string | null {
  if (confirmed) return null;
  return previous === 'unrecorded' ? SEND_LOCKED_AFTER_SENT_MESSAGE : SEND_OUTCOME_UNKNOWN_MESSAGE;
}

/**
 * The confirmation check for an already_sent candidate: true when the reconciled row's SendCount
 * rose above the row the send started from, so the send happened. False NEVER means "not sent":
 * an outcome-unknown send is never stamped, so an unchanged count leaves the outcome unknown.
 */
export function sendCountIncreased(
  before: Pick<AdminInvitation, 'sendCount'>,
  after: Pick<AdminInvitation, 'sendCount'> | undefined,
): boolean {
  return after !== undefined && after.sendCount > before.sendCount;
}

export function sendBlockMessage(block: SendBlock): string {
  if (block.kind === 'cooldown') {
    if (block.cause === 'rate_limited') {
      return `The email service is rate-limiting invitations, so nothing was sent. You can try again in ${block.secondsLeft} s`;
    }
    if (block.cause === 'unspecified') return `Try again in ${block.secondsLeft} s`;
    return `Sent moments ago. You can resend in ${block.secondsLeft} s`;
  }
  if (block.kind === 'in_progress') return SEND_IN_PROGRESS_MESSAGE;
  return `This invitation has been sent ${block.sendCount} times. Revoke it and create a new one.`;
}

/**
 * Counts `seconds` down once per second, calling `onChange` with the seconds left (immediately
 * with the start value, finally with 0). Returns a cancel function. Nothing is resent at 0: the
 * control is simply enabled again.
 */
export function startSendCooldown(seconds: number, onChange: (secondsLeft: number) => void): () => void {
  let secondsLeft = Math.max(0, Math.ceil(seconds));
  onChange(secondsLeft);
  if (secondsLeft === 0) return () => undefined;
  const timer = setInterval(() => {
    secondsLeft -= 1;
    onChange(secondsLeft);
    if (secondsLeft <= 0) clearInterval(timer);
  }, 1000);
  return () => clearInterval(timer);
}

/**
 * The per-invitation block map after a cooldown tick: present while seconds remain, gone at 0.
 * `cause` is the too_soon failure's, so a rate-limit countdown keeps its own wording.
 */
export function applyCooldownTick(
  blocks: Record<string, SendBlock>,
  invitationId: string,
  secondsLeft: number,
  cause?: SendCooldownCause,
): Record<string, SendBlock> {
  if (secondsLeft <= 0) {
    if (!(invitationId in blocks)) return blocks;
    const next = { ...blocks };
    delete next[invitationId];
    return next;
  }
  return { ...blocks, [invitationId]: cause ? { kind: 'cooldown', secondsLeft, cause } : { kind: 'cooldown', secondsLeft } };
}

// ---- Ordered invitations reads (OrgDetailPage) ----

/**
 * 'applied': this answer is in state; 'superseded': a later read was issued, or a send DTO was
 * applied, after this read started, or the page no longer shows this read's org, so its answer
 * was discarded (or the read was never issued); 'failed': the latest read failed.
 */
export type InvitationsReadResult = 'applied' | 'superseded' | 'failed';

export interface InvitationsReader {
  /**
   * Reads `orgId`'s list and applies the answer only if nothing superseded it meanwhile and
   * `orgId` is still the page's org. A read requested for an org the page has left is not
   * issued, so it neither applies nor takes the sequence from the current org's reads.
   */
  read: (orgId: string) => Promise<InvitationsReadResult>;
  /**
   * Called before a send DTO is applied to the list. When `orgId` is the page's org, every read
   * still in flight is now older than state, and it returns true; for any other org it changes
   * nothing and returns false, and the caller drops the DTO.
   */
  supersede: (orgId: string) => boolean;
}

/**
 * The org detail page's invitations reads, ordered by a monotonically increasing sequence, the
 * pattern OrgDetailPage already uses for the usage summary (`usageSummarySeqRef`): a
 * response whose sequence is older than the latest issued is discarded, so an older reload that
 * resolves last cannot restore a link that a newer reload or a send DTO already replaced, and a
 * failure is reported only when it belongs to the latest read. Every read and every supersede is
 * also scoped to the page's CURRENT org: work the previous org's controller started before
 * the route changed can neither issue a read that takes the newest sequence nor land in the new
 * org's list.
 */
export function createInvitationsReader(deps: {
  list: (orgId: string) => Promise<AdminInvitation[]>;
  /** The org the page shows right now (OrgDetailPage's route param); every other org is discarded. */
  currentOrgId: () => string | undefined;
  /** Receives the list with the org it was read for. */
  onLoaded: (invitations: AdminInvitation[], orgId: string) => void;
  onFailed: (err: unknown) => void;
}): InvitationsReader {
  let latest = 0;
  const isLatest = (orgId: string, seq: number) => seq === latest && deps.currentOrgId() === orgId;
  return {
    read: async (orgId) => {
      if (deps.currentOrgId() !== orgId) return 'superseded';
      const seq = ++latest;
      try {
        const invitations = await deps.list(orgId);
        if (!isLatest(orgId, seq)) return 'superseded';
        deps.onLoaded(invitations, orgId);
        return 'applied';
      } catch (err: unknown) {
        if (!isLatest(orgId, seq)) return 'superseded';
        deps.onFailed(err);
        return 'failed';
      }
    },
    supersede: (orgId) => {
      if (deps.currentOrgId() !== orgId) return false;
      latest += 1;
      return true;
    },
  };
}
